import { supabase } from '@/integrations/supabase/client';

export type AgendamentoStatus = 'PENDENTE' | 'CONFIRMADO' | 'REALIZADO' | 'CANCELADO' | 'FALTOU' | 'NOVA_TENTATIVA' | 'REAGENDADO';

export interface Agendamento {
  id: number;
  tenant_id: number;
  cliente_id?: number;
  relacionador_id?: number;
  lider_vendas_id?: number;
  data_agendamento: string; // YYYY-MM-DD
  hora_agendamento: string; // HH:MM
  status: AgendamentoStatus;
  observacoes?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  // Relacionamentos
  cliente?: {
    id: number;
    nome: string;
    email?: string;
    tel1?: string;
    tel2?: string;
  };
  relacionador?: {
    id: number;
    nome: string;
  };
  lider_vendas?: {
    id: number;
    nome: string;
  };
}

export interface AgendamentoInsert {
  tenant_id: number;
  cliente_id?: number;
  relacionador_id?: number;
  lider_vendas_id?: number;
  data_agendamento: string;
  hora_agendamento: string;
  status?: AgendamentoStatus;
  observacoes?: string;
  created_by?: string;
}

export interface AgendamentoUpdate {
  cliente_id?: number;
  relacionador_id?: number;
  lider_vendas_id?: number;
  data_agendamento?: string;
  hora_agendamento?: string;
  status?: AgendamentoStatus;
  observacoes?: string;
}

export interface ListarAgendamentosResponse {
  data: Agendamento[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface AgendamentosFiltros {
  relacionador_id?: number;
  data_inicio?: string;
  data_fim?: string;
  status?: AgendamentoStatus;
  cliente_search?: string;
  page?: number;
  per_page?: number;
}

export class AgendamentosService {
  /**
   * Lista agendamentos com paginação e filtros
   */
  async listarAgendamentos(
    page = 1,
    per_page = 10,
    filtros?: AgendamentosFiltros,
    tenantId?: string,
    filterByUserId?: string
  ): Promise<ListarAgendamentosResponse> {
    try {
      // Query para contar total
      let countQuery = supabase
        .from('agendamentos')
        .select('*', { count: 'exact', head: true });

      // Query para buscar dados
      let dataQuery = supabase
        .from('agendamentos')
        .select(`
          id,
          tenant_id,
          cliente_id,
          relacionador_id,
          lider_vendas_id,
          data_agendamento,
          hora_agendamento,
          status,
          observacoes,
          created_at,
          updated_at,
          created_by,
          cliente:clientes(id, nome, email, tel1, tel2),
          relacionador:consultores!agendamentos_relacionador_id_fkey(id, nome),
          lider_vendas:consultores!agendamentos_lider_vendas_id_fkey(id, nome)
        `);

      // Aplicar filtro de tenant
      if (tenantId) {
        const tId = typeof tenantId === 'string' ? parseInt(tenantId, 10) : tenantId;
        if (!isNaN(tId)) {
          countQuery = countQuery.eq('tenant_id', tId);
          dataQuery = dataQuery.eq('tenant_id', tId);
        }
      }

      // Aplicar visibilidade baseada no papel se filterByUserId for fornecido
      if (filterByUserId) {
        const { data: userData } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', filterByUserId as string)
          .maybeSingle();

        if (userData && !['SUPER_ADMIN', 'ASSOCIADO', 'SECRETARIA', 'MASTER'].includes(userData.role as string)) {
          const { data: consultor } = await supabase
            .from('consultores')
            .select('id, supervisor_id, tipo')
            .eq('user_id', filterByUserId)
            .maybeSingle();

          if (consultor) {
            const role = userData.role || (consultor.tipo ? consultor.tipo.toUpperCase() : null);

            if (role === 'RELACIONADOR') {
              // Relacionador vê seus próprios agendamentos
              countQuery = countQuery.eq('relacionador_id', consultor.id);
              dataQuery = dataQuery.eq('relacionador_id', consultor.id);
            } else if (role === 'LIDER_VENDAS' || role === 'LIDER') {
              // Líder vê agendamentos onde é o lider ou agendamentos dos relacionadores de seu time
              const { data: timeConsultores } = consultor.supervisor_id ? await supabase
                .from('consultores')
                .select('id')
                .eq('supervisor_id', Number(consultor.supervisor_id)) : { data: [] };

              const consultorIds = (timeConsultores || []).map(t => t.id);
              consultorIds.push(consultor.id);

              if (consultorIds.length > 0) {
                countQuery = countQuery.or(`lider_vendas_id.eq.${consultor.id},relacionador_id.in.(${consultorIds.join(',')})`);
                dataQuery = dataQuery.or(`lider_vendas_id.eq.${consultor.id},relacionador_id.in.(${consultorIds.join(',')})`);
              } else {
                countQuery = countQuery.eq('lider_vendas_id', consultor.id);
                dataQuery = dataQuery.eq('lider_vendas_id', consultor.id);
              }
            } else if (role === 'SUPERVISOR') {
              // Supervisor vê agendamentos do seu time
              const { data: timeConsultores } = await supabase
                .from('consultores')
                .select('id')
                .eq('supervisor_id', consultor.id);

              const consultorIds = (timeConsultores || []).map(t => t.id);
              consultorIds.push(consultor.id);

              if (consultorIds.length > 0) {
                countQuery = countQuery.or(`relacionador_id.in.(${consultorIds.join(',')}),lider_vendas_id.in.(${consultorIds.join(',')})`);
                dataQuery = dataQuery.or(`relacionador_id.in.(${consultorIds.join(',')}),lider_vendas_id.in.(${consultorIds.join(',')})`);
              }
            }
          }
        }
      }

      // Aplicar filtros
      if (filtros) {
        // Filtrar por período de data
        if (filtros.data_inicio) {
          countQuery = countQuery.gte('data_agendamento', filtros.data_inicio);
          dataQuery = dataQuery.gte('data_agendamento', filtros.data_inicio);
        }

        if (filtros.data_fim) {
          countQuery = countQuery.lte('data_agendamento', filtros.data_fim);
          dataQuery = dataQuery.lte('data_agendamento', filtros.data_fim);
        }

        // Filtrar por nome do cliente
        if (filtros.cliente_search) {
          // Buscar cliente_ids que correspondem ao search
          const { data: clientes } = await supabase
            .from('clientes')
            .select('id')
            .or(`nome.ilike.%${filtros.cliente_search}%,tel1.ilike.%${filtros.cliente_search}%,tel2.ilike.%${filtros.cliente_search}%`);

          if (clientes && clientes.length > 0) {
            const clienteIds = clientes.map(c => c.id);
            countQuery = countQuery.in('cliente_id', clienteIds);
            dataQuery = dataQuery.in('cliente_id', clienteIds);
          } else {
            // Nenhum cliente encontrado, retornar vazio
            return {
              data: [],
              total: 0,
              page,
              per_page,
              total_pages: 0
            };
          }
        }
      }

      // Executar count
      const { count, error: countError } = await countQuery;
      if (countError) throw countError;

      // Aplicar ordenação e paginação
      const startIndex = (page - 1) * per_page;
      dataQuery = dataQuery
        .order('data_agendamento', { ascending: false })
        .order('hora_agendamento', { ascending: false })
        .range(startIndex, startIndex + per_page - 1);

      // Executar query de dados
      const { data, error } = await dataQuery;

      if (error) throw error;

      const total = count || 0;
      const total_pages = Math.ceil(total / per_page);

      return {
        data: (data || []).map(this.mapearAgendamento),
        total,
        page,
        per_page,
        total_pages
      };
    } catch (error) {
      console.error('Erro ao listar agendamentos:', error);
      throw error;
    }
  }

  /**
   * Busca agendamento por ID
   */
  async buscarAgendamento(id: number, filterByUserId?: string): Promise<Agendamento | null> {
    try {
      let query = supabase
        .from('agendamentos')
        .select(`
          *,
          cliente:clientes(id, nome, email, tel1, tel2),
          relacionador:consultores!agendamentos_relacionador_id_fkey(id, nome),
          lider_vendas:consultores!agendamentos_lider_vendas_id_fkey(id, nome)
        `)
        .eq('id', id);

      // Aplicar filtros baseados no usuário (mesma lógica da listagem)
      if (filterByUserId) {
        // Verificar primeiro o papel do usuário
        const { data: userData } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', filterByUserId)
          .maybeSingle();

        // Se for admin ou secretaria, não filtrar por consultor
        if (userData && !['SUPER_ADMIN', 'ASSOCIADO', 'SECRETARIA'].includes(userData.role as string)) {
          // Buscar o consultor_id e tipo do usuário
          const { data: consultor } = await supabase
            .from('consultores')
            .select('id, tipo, supervisor_id')
            .eq('user_id', filterByUserId)
            .single();

          if (consultor) {
            // Se for relacionador, filtrar por relacionador_id
            if (consultor.tipo === 'relacionador') {
              query = query.eq('relacionador_id', consultor.id);
            }
            // Se for líder de vendas, permitir acesso aos agendamentos dos relacionadores da equipe
            else if (consultor.tipo === 'lider' && consultor.supervisor_id) {
              // Buscar relacionadores da mesma equipe
              const { data: relacionadores } = await supabase
                .from('consultores')
                .select('id')
                .eq('supervisor_id', Number(consultor.supervisor_id))
                .eq('tipo', 'relacionador')
                .eq('status', 'ativo');

              if (relacionadores && relacionadores.length > 0) {
                const relacionadorIds = relacionadores.map(r => r.id);
                // Líder pode ver agendamentos onde ele é o líder OU onde o relacionador é da sua equipe
                query = query.or(`lider_vendas_id.eq.${consultor.id},relacionador_id.in.(${relacionadorIds.join(',')})`);
              } else {
                // Fallback: apenas agendamentos onde ele é o líder
                query = query.eq('lider_vendas_id', consultor.id);
              }
            }
            // Outros tipos (como supervisor) podem ver tudo
          }
        }

      }

      const { data, error } = await query.single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }

      return data ? this.mapearAgendamento(data) : null;
    } catch (error) {
      console.error('Erro ao buscar agendamento:', error);
      throw error;
    }
  }

  /**
   * Cria novo agendamento
   */
  async criarAgendamento(dados: AgendamentoInsert): Promise<Agendamento> {
    try {
      const { data, error } = await supabase
        .from('agendamentos')
        .insert(dados)
        .select(`
          *,
          cliente:clientes(id, nome, email, tel1, tel2),
          relacionador:consultores!agendamentos_relacionador_id_fkey(id, nome),
          lider_vendas:consultores!agendamentos_lider_vendas_id_fkey(id, nome)
        `)
        .single();

      if (error) throw error;

      return this.mapearAgendamento(data);
    } catch (error) {
      console.error('Erro ao criar agendamento:', error);
      throw error;
    }
  }

  /**
   * Atualiza agendamento
   */
  async atualizarAgendamento(id: number, dados: AgendamentoUpdate): Promise<Agendamento> {
    try {
      const { data, error } = await supabase
        .from('agendamentos')
        .update(dados)
        .eq('id', id)
        .select(`
          *,
          cliente:clientes(id, nome, email, tel1, tel2),
          relacionador:consultores!agendamentos_relacionador_id_fkey(id, nome),
          lider_vendas:consultores!agendamentos_lider_vendas_id_fkey(id, nome)
        `)
        .single();

      if (error) throw error;

      return this.mapearAgendamento(data);
    } catch (error) {
      console.error('Erro ao atualizar agendamento:', error);
      throw error;
    }
  }

  /**
   * Deleta agendamento (soft delete ou hard delete)
   */
  async deletarAgendamento(id: number): Promise<void> {
    try {
      const { error } = await supabase
        .from('agendamentos')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao deletar agendamento:', error);
      throw error;
    }
  }

  /**
   * Busca estatísticas de agendamentos
   */
  async buscarEstatisticas(
    periodo: 'hoje' | 'semana' | 'mes' | 'ano' | 'todos',
    consultorId?: number,
    tenantId?: string,
    tipoConsultor?: 'relacionador' | 'lider' | 'supervisor'
  ): Promise<{
    total_agendamentos: number;
    agendamentos_realizados: number;
    agendamentos_cancelados: number;
    agendamentos_faltaram: number;
    taxa_comparecimento: number;
  }> {
    try {
      // Calcular período
      const hoje = new Date();
      let dataInicio: Date | null = null;

      switch (periodo) {
        case 'hoje':
          dataInicio = new Date(hoje);
          dataInicio.setHours(0, 0, 0, 0);
          break;
        case 'semana':
          dataInicio = new Date(hoje);
          dataInicio.setDate(hoje.getDate() - 7);
          break;
        case 'mes':
          dataInicio = new Date(hoje);
          dataInicio.setMonth(hoje.getMonth() - 1);
          break;
        case 'ano':
          dataInicio = new Date(hoje);
          dataInicio.setFullYear(hoje.getFullYear() - 1);
          break;
        case 'todos':
          dataInicio = null;
          break;
      }

      // Buscar todos os agendamentos (RLS vai filtrar automaticamente)
      let query = supabase
        .from('agendamentos')
        .select('id, status');

      if (tenantId) {
        // Converter para número se necessário
        const tenantIdNum = typeof tenantId === 'string' ? parseInt(tenantId, 10) : tenantId;
        if (!isNaN(tenantIdNum)) {
          query = query.eq('tenant_id', tenantIdNum);
        }
      }

      // Aplicar visibilidade nas estatísticas
      if (consultorId) {
        if (tipoConsultor === 'relacionador') {
          query = query.eq('relacionador_id', consultorId);
        } else if (tipoConsultor === 'lider') {
          // Líder vê seus próprios agendamentos e os dos relacionadores sob o mesmo supervisor
          const { data: consultor } = await supabase
            .from('consultores')
            .select('supervisor_id')
            .eq('id', consultorId)
            .maybeSingle();

          if (consultor) {
            const { data: timeIds } = consultor.supervisor_id ? await supabase
              .from('consultores')
              .select('id')
              .eq('supervisor_id', Number(consultor.supervisor_id)) : { data: [] };

            const ids = (timeIds || []).map(t => t.id);
            ids.push(consultorId);

            if (ids.length > 0) {
              const idFilter = `(${ids.join(',')})`;
              query = query.or(`relacionador_id.in.${idFilter},lider_vendas_id.in.${idFilter}`);
            } else {
              query = query.eq('lider_vendas_id', consultorId);
            }
          } else {
            query = query.eq('lider_vendas_id', consultorId);
          }
        } else if (tipoConsultor === 'supervisor') {
          // Para supervisores, buscamos agendamentos onde ele é o supervisor do relacionador ou líder
          const { data: timeIds } = await supabase
            .from('consultores')
            .select('id')
            .eq('supervisor_id', consultorId);

          const ids = (timeIds || []).map(t => t.id);
          ids.push(consultorId);

          if (ids.length > 0) {
            const idFilter = `(${ids.join(',')})`;
            query = query.or(`relacionador_id.in.${idFilter},lider_vendas_id.in.${idFilter}`);
          }
        }
      }

      if (dataInicio) {
        query = query.gte('data_agendamento', dataInicio.toISOString().split('T')[0]);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao buscar estatísticas de agendamentos:', error);
        throw error;
      }

      const agendamentos = data || [];
      const total = agendamentos.length;
      const realizados = agendamentos.filter(a => a.status === 'REALIZADO').length;
      const cancelados = agendamentos.filter(a => a.status === 'CANCELADO').length;
      const faltaram = agendamentos.filter(a => a.status === 'FALTOU').length;
      const taxaComparecimento = total > 0 ? (realizados / total) * 100 : 0;

      return {
        total_agendamentos: total,
        agendamentos_realizados: realizados,
        agendamentos_cancelados: cancelados,
        agendamentos_faltaram: faltaram,
        taxa_comparecimento: taxaComparecimento
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      throw error;
    }
  }

  /**
   * Verifica se já existe um agendamento para o mesmo líder de vendas no mesmo horário
   */
  async verificarConflito(
    dataAgendamento: string,
    horaAgendamento: string,
    liderVendasId: number,
    agendamentoIdParaExcluir?: number
  ): Promise<boolean> {
    try {
      // Normalizar hora para HH:mm se necessário (alguns bancos retornam HH:mm:ss)
      const horaNormalizada = horaAgendamento.slice(0, 5);

      let query = supabase
        .from('agendamentos')
        .select('id')
        .eq('data_agendamento', dataAgendamento)
        .eq('hora_agendamento', horaNormalizada)
        .eq('lider_vendas_id', liderVendasId)
        .not('status', 'in', '("CANCELADO","FALTOU")'); // Status que não bloqueiam horário

      if (agendamentoIdParaExcluir) {
        query = query.neq('id', agendamentoIdParaExcluir);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (data && data.length > 0);
    } catch (error) {
      console.error('Erro ao verificar conflito de agendamento:', error);
      throw error;
    }
  }

  /**
   * Mapeia dados do banco para o formato Agendamento
   */
  private mapearAgendamento(data: any): Agendamento {
    return {
      id: data.id,
      tenant_id: data.tenant_id,
      cliente_id: data.cliente_id,
      relacionador_id: data.relacionador_id,
      lider_vendas_id: data.lider_vendas_id,
      data_agendamento: data.data_agendamento,
      hora_agendamento: data.hora_agendamento?.substring(0, 5) || '', // Normalizar para HH:mm
      status: data.status as AgendamentoStatus,
      observacoes: data.observacoes,
      created_at: data.created_at,
      updated_at: data.updated_at,
      created_by: data.created_by,
      cliente: data.cliente,
      relacionador: data.relacionador,
      lider_vendas: data.lider_vendas
    };
  }
}

export const agendamentosService = new AgendamentosService();

